import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import Webcam from "react-webcam";
import {
  loginWithEmailThunk,
  loginWithGoogleThunk,
  loginWithFaceThunk,
  clearError,
} from "../../redux/slices/authSlice";
import { addNotification } from "../../redux/slices/uiSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { AuthProvider } from "../../types";
import { validateEmail } from "../../utils/validation.utils";
import { Button } from "../common/Button";
import { Input } from "../common/Input";
import { Spinner } from "../common/Spinner";
import { FaceFrame } from "./FaceFrame";
import { Mail, Chrome, ScanFace, Eye, EyeOff } from "lucide-react";

interface LoginFormProps {
  onSwitchToRegister: () => void;
  defaultMethod?: AuthProvider;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToRegister,
  defaultMethod = "email",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, user, initialCheckDone } =
    useSelector((state: RootState) => state.auth);

  const [authMethod, setAuthMethod] = useState<AuthProvider>(defaultMethod);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Track if user just logged in (not session restoration)
  const [hasJustLoggedIn, setHasJustLoggedIn] = useState(false);

  // Face auth state
  const webcamRef = useRef<Webcam>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // Update auth method when defaultMethod prop changes
  useEffect(() => {
    if (defaultMethod) {
      setAuthMethod(defaultMethod);
    }
  }, [defaultMethod]);

  // Handle successful login - show toast and redirect ONLY for new logins
  useEffect(() => {
    if (isAuthenticated && user && initialCheckDone && hasJustLoggedIn) {
      dispatch(
        addNotification({
          type: "success",
          message: `Welcome back, ${user.first_name || user.full_name}!`,
        })
      );

      const from = location.state?.from?.pathname;
      if (from && from !== "/login" && from !== "/register") {
        navigate(from, { replace: true });
      } else {
        navigate(user.role === "admin" ? "/admin" : "/dashboard", {
          replace: true,
        });
      }
    }
  }, [
    isAuthenticated,
    user,
    initialCheckDone,
    hasJustLoggedIn,
    dispatch,
    navigate,
    location,
  ]);

  // If already authenticated (session restored), redirect immediately without toast
  useEffect(() => {
    if (isAuthenticated && initialCheckDone && !hasJustLoggedIn) {
      const from = location.state?.from?.pathname;
      if (from && from !== "/login" && from !== "/register") {
        navigate(from, { replace: true });
      } else {
        navigate(user?.role === "admin" ? "/admin" : "/dashboard", {
          replace: true,
        });
      }
    }
  }, [
    isAuthenticated,
    initialCheckDone,
    hasJustLoggedIn,
    user,
    navigate,
    location,
  ]);

  const validateEmailForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!validateEmail(email)) {
      errors.email = "Invalid email format";
    }

    if (!password) {
      errors.password = "Password is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());

    if (!validateEmailForm()) return;

    setHasJustLoggedIn(true);
    dispatch(loginWithEmailThunk({ email, password }));
  };

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      setHasJustLoggedIn(true);
      dispatch(
        loginWithGoogleThunk({ googleToken: credentialResponse.credential })
      );
    }
  };

  const handleGoogleError = () => {
    setValidationErrors({ google: "Google sign-in failed. Please try again." });
  };

  const handleFaceCapture = useCallback(async () => {
    if (!webcamRef.current) return;

    setIsCapturing(true);
    const imageSrc = webcamRef.current.getScreenshot();

    if (imageSrc) {
      const base64Data = imageSrc.split(",")[1];
      setHasJustLoggedIn(true);
      dispatch(loginWithFaceThunk({ faceImage: base64Data }));
    }

    setIsCapturing(false);
  }, [dispatch]);

  const authMethods = [
    {
      id: "email" as AuthProvider,
      label: "Email",
      icon: <Mail className="w-4 h-4" />,
    },
    {
      id: "google" as AuthProvider,
      label: "Google",
      icon: <Chrome className="w-4 h-4" />,
    },
    {
      id: "face" as AuthProvider,
      label: "Face ID",
      icon: <ScanFace className="w-4 h-4" />,
    },
  ];

  const renderAuthMethodTabs = () => (
    <div className="flex gap-1.5 p-1 bg-slate-900/50 rounded-lg mb-4">
      {authMethods.map((method) => (
        <button
          key={method.id}
          type="button"
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium rounded-md transition-all duration-200 ${
            authMethod === method.id
              ? "bg-gradient-to-r from-primary-500/20 to-accent-500/20 text-primary-400 border border-primary-500/30"
              : "text-slate-400 hover:text-slate-300 hover:bg-slate-800/50"
          }`}
          onClick={() => setAuthMethod(method.id)}
        >
          {method.icon}
          <span>{method.label}</span>
        </button>
      ))}
    </div>
  );

  const renderEmailForm = () => (
    <div className="space-y-3">
      <form onSubmit={handleEmailLogin} className="space-y-3">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          error={validationErrors.email}
          disabled={loading}
        />

        <Input
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter password"
          error={validationErrors.password}
          disabled={loading}
          rightIcon={
            <button
              type="button"
              className="text-slate-400 hover:text-slate-300 transition-colors"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          }
        />

        {error && (
          <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
            {error}
          </div>
        )}

        <Button type="submit" variant="gradient" fullWidth disabled={loading}>
          {loading ? <Spinner size="sm" /> : "Sign In"}
        </Button>
      </form>
    </div>
  );

  const renderGoogleForm = () => (
    <div className="space-y-4 py-2">
      <p className="text-slate-400 text-xs text-center">
        Quick access with your Google account
      </p>

      <div className="flex justify-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          theme="filled_black"
          size="large"
          width="260"
        />
      </div>

      {(validationErrors.google || error) && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center">
          {validationErrors.google || error}
        </div>
      )}
    </div>
  );

  const renderFaceForm = () => (
    <div className="space-y-3">
      <p className="text-slate-400 text-xs text-center">
        Position your face in frame.{" "}
        <span className="text-primary-400">No password needed!</span>
      </p>

      {/* Compact Webcam */}
      <div className="relative rounded-lg overflow-hidden bg-slate-900 w-full max-w-[220px] mx-auto aspect-[4/3] border border-slate-700/50">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={{ width: 220, height: 165, facingMode: "user" }}
          onUserMedia={() => setIsCameraReady(true)}
          onUserMediaError={() => setIsCameraReady(false)}
          className="w-full h-full object-cover"
        />
        <FaceFrame size="small" />
      </div>

      {error && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center">
          {error}
        </div>
      )}

      <Button
        type="button"
        variant="gradient"
        fullWidth
        onClick={handleFaceCapture}
        disabled={loading || !isCameraReady || isCapturing}
        icon={<ScanFace className="w-4 h-4" />}
      >
        {loading || isCapturing ? <Spinner size="sm" /> : "Capture & Sign In"}
      </Button>

      <p className="text-[10px] text-slate-500 text-center">
        Demo purposes only
      </p>
    </div>
  );

  // Show loading while checking initial auth
  if (!initialCheckDone) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Compact Header */}
      <div className="text-center mb-3">
        <h2 className="text-lg font-bold text-white">Welcome Back</h2>
        <p className="text-slate-400 text-xs">Sign in to your account</p>
      </div>

      {renderAuthMethodTabs()}

      {authMethod === "email" && renderEmailForm()}
      {authMethod === "google" && renderGoogleForm()}
      {authMethod === "face" && renderFaceForm()}

      <div className="mt-4 pt-4 border-t border-slate-700/50 text-center">
        <span className="text-slate-400 text-xs">Don't have an account? </span>
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="text-primary-400 hover:text-primary-300 font-semibold text-xs transition-colors"
        >
          Sign Up
        </button>
      </div>
    </div>
  );
};
